"use client";

import Link from "next/link";
import { SideNav } from "@/components/SideNav";

export default function GuideClient() {
  return (
    <>
      <SideNav />
      <article className="guide-page">
        <div className="guide-container">
          <nav className="guide-breadcrumb">
            <Link href="/">Home</Link>
            <span>/</span>
            <span>Guide</span>
          </nav>

          <header className="guide-header">
            <h1>How to Sell Digital Content with Cryptocurrency</h1>
            <p className="guide-intro">
              Selling digital content online usually means setting up a merchant
              account, dealing with payment processors, and waiting days for
              payouts. With cryptocurrency, you can skip all of that. This guide
              shows you how to sell digital files using USDC stablecoin payments
              on the Base blockchain.
            </p>
          </header>

          <section className="guide-section">
            <h2>Why Use Crypto for Digital Content Sales?</h2>
            <p>
              Traditional payment processors require merchant account
              applications, bank approvals, and hold your funds for days or
              weeks. Cryptocurrency payments remove these barriers:
            </p>
            <ul className="guide-list">
              <li>
                <strong>Instant payouts</strong> &mdash; Funds go directly to
                your wallet the moment a buyer pays. No waiting period, no
                payout schedule.
              </li>
              <li>
                <strong>No merchant account</strong> &mdash; You only need a
                crypto wallet (like Coinbase Wallet or MetaMask) to start
                receiving payments. No bank approval, no paperwork.
              </li>
              <li>
                <strong>Global by default</strong> &mdash; Anyone with a crypto
                wallet can pay you, regardless of country or banking
                restrictions.
              </li>
              <li>
                <strong>Stable value</strong> &mdash; USDC is pegged 1:1 to the
                US Dollar, so you don&apos;t need to worry about price
                volatility.
              </li>
              <li>
                <strong>No chargebacks</strong> &mdash; Blockchain transactions
                are final. No fraud disputes or reversed payments.
              </li>
            </ul>
          </section>

          <section className="guide-section">
            <h2>What You Can Sell</h2>
            <p>
              Any digital file up to 10 MB can be sold through a payment link.
              Popular content types include:
            </p>
            <ul className="guide-list">
              <li>E-books and PDF guides</li>
              <li>Design templates and graphics</li>
              <li>Source code and development tools</li>
              <li>Music, samples, and audio files</li>
              <li>Photography presets and filters</li>
              <li>Exclusive written content and newsletters</li>
              <li>Educational materials and courses</li>
            </ul>
          </section>

          <section className="guide-section">
            <h2>Step-by-Step: Create Your First Payment Link</h2>

            <h3>Step 1: Set Up a Wallet</h3>
            <p>
              If you don&apos;t have a crypto wallet yet, download{" "}
              <a
                href="https://www.coinbase.com/wallet"
                target="_blank"
                rel="noopener noreferrer"
              >
                Coinbase Wallet
              </a>{" "}
              (recommended for beginners) or{" "}
              <a
                href="https://metamask.io"
                target="_blank"
                rel="noopener noreferrer"
              >
                MetaMask
              </a>
              . Make sure it&apos;s connected to the Base network. Your wallet
              address is where you&apos;ll receive payments.
            </p>

            <h3>Step 2: Prepare Your Content</h3>
            <p>
              Organize the files you want to sell. You can upload up to 20 files
              per link (10 MB total). Files are automatically compressed into a
              ZIP archive for buyers to download.
            </p>

            <h3>Step 3: Create the Payment Link</h3>
            <p>
              Go to the{" "}
              <Link href="/seller">Create PayLink page</Link> and fill in:
            </p>
            <ul className="guide-list">
              <li>
                <strong>Title</strong> &mdash; What you&apos;re selling
              </li>
              <li>
                <strong>Description</strong> &mdash; Brief details about the
                content
              </li>
              <li>
                <strong>Files</strong> &mdash; Upload the digital files
              </li>
              <li>
                <strong>Price</strong> &mdash; Set your price in USDC (minimum
                $1)
              </li>
              <li>
                <strong>Payout address</strong> &mdash; Your wallet address for
                receiving funds
              </li>
            </ul>

            <h3>Step 4: Share Your Link</h3>
            <p>
              Once created, you get a unique URL you can share anywhere: social
              media, email, your website, messaging apps. When someone opens the
              link and pays, they instantly get access to your content, and you
              instantly get paid.
            </p>
          </section>

          <section className="guide-section">
            <h2>Tips for Maximizing Sales</h2>
            <ul className="guide-list">
              <li>
                <strong>Write clear descriptions</strong> &mdash; Help buyers
                understand exactly what they&apos;re getting before they pay.
              </li>
              <li>
                <strong>Set fair prices</strong> &mdash; Research what similar
                content sells for. Starting lower can help build initial sales
                and reviews.
              </li>
              <li>
                <strong>Share in relevant communities</strong> &mdash; Post your
                link where your target audience hangs out (Discord, Twitter,
                Reddit, Telegram).
              </li>
              <li>
                <strong>Provide value</strong> &mdash; Quality content leads to
                word-of-mouth referrals and repeat customers.
              </li>
            </ul>
          </section>

          <section className="guide-section">
            <h2>Understanding the Costs</h2>
            <p>
              Govilo charges a <strong>5% tool usage fee</strong> on successful
              transactions. There are no monthly fees, setup costs, or hidden
              charges. Buyers pay a small gas fee (approximately $0.1) for the
              blockchain transaction.
            </p>
            <p>
              For example, if you sell a digital template for 10 USDC, you
              receive 9.50 USDC directly in your wallet. The buyer pays 10 USDC
              plus roughly $0.1 in gas fees.
            </p>
          </section>

          <section className="guide-section">
            <h2>Ready to Start Selling?</h2>
            <p>
              Creating your first payment link takes less than 2 minutes. No
              sign-up forms, no approval process, no waiting.
            </p>
            <div className="guide-cta">
              <Link href="/seller" className="btn-primary">
                Create Your First PayLink
              </Link>
            </div>
            <p>
              Have questions? Visit our{" "}
              <Link href="/support">Support page</Link> for help getting
              started.
            </p>
          </section>
        </div>
      </article>
    </>
  );
}
